import type { InterviewPhase } from '@/lib/interviewPhase';

export type ConversationTurn = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  phase?: InterviewPhase;
  /**
   * Original transcript entry ids that were merged into this turn.
   * Present when produced by `mergeConsecutiveSameRoleTurns`.
   */
  sourceIds?: string[];
};

export type MainQuestionFollowupStat = {
  index: number;
  question: string;
  probingFollowups: number;
  /**
   * 0-based index among merged interviewing-phase assistant turns
   * (same numbering as the analyze classifier prompt). Optional for legacy analyses.
   */
  assistantTurnIndex?: number;
  /**
   * Raw transcript entry id to badge in the UI (last bubble of a merged AI turn).
   * Optional for legacy analyses.
   */
  turnId?: string;
};

export type ConversationAnalysis = {
  /**
   * Conversational turn counts within interviewing phase.
   * Consecutive same-role transcript entries are merged into one turn.
   */
  turns: {
    total: number;
    assistant: number;
    user: number;
  };
  /** Length averages over merged interviewing-phase turns. */
  avgWords: {
    assistant: number;
    user: number;
  };
  avgChars: {
    assistant: number;
    user: number;
  };
  mainQuestions: MainQuestionFollowupStat[];
  analyzedAt: string;
};

export function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function countChars(text: string): number {
  return text.replace(/\s+/g, '').length;
}

function average(values: number[]): number {
  if (values.length === 0) return 0;
  const sum = values.reduce((a, b) => a + b, 0);
  return Math.round((sum / values.length) * 10) / 10;
}

export function isInterviewingTurn(turn: ConversationTurn): boolean {
  // Legacy transcripts without phase: treat as interviewing so analysis still works.
  return !turn.phase || turn.phase === 'interviewing';
}

/** Keep only interviewing-phase turns (opening/closing excluded). */
export function filterInterviewingTurns(transcript: ConversationTurn[]): ConversationTurn[] {
  return transcript.filter(isInterviewingTurn);
}

/**
 * Merge consecutive same-role entries into one conversational turn.
 * Text is joined with a blank line so multi-bubble AI speech stays readable.
 */
export function mergeConsecutiveSameRoleTurns(
  transcript: ConversationTurn[],
): ConversationTurn[] {
  const merged: ConversationTurn[] = [];
  for (const entry of transcript) {
    const entrySources = entry.sourceIds?.length ? entry.sourceIds : [entry.id];
    const prev = merged[merged.length - 1];
    if (prev && prev.role === entry.role) {
      const combined = `${prev.text.trim()}\n\n${entry.text.trim()}`.trim();
      const prevSources = prev.sourceIds?.length ? prev.sourceIds : [prev.id];
      merged[merged.length - 1] = {
        ...prev,
        id: `${prev.id}+${entry.id}`,
        text: combined,
        sourceIds: [...prevSources, ...entrySources],
        // Prefer interviewing if either piece is interviewing (shouldn't mix phases often).
        phase: prev.phase === 'interviewing' || entry.phase === 'interviewing'
          ? 'interviewing'
          : prev.phase ?? entry.phase,
      };
    } else {
      merged.push({ ...entry, sourceIds: [...entrySources] });
    }
  }
  return merged;
}

/** Map raw transcript entry id → "Q1" / "Q2" / … from analysis (empty if labels absent). */
export function buildMainQuestionLabelByTurnId(
  analysis: ConversationAnalysis | null | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  if (!analysis?.mainQuestions) return map;
  for (const q of analysis.mainQuestions) {
    if (q.turnId) map.set(q.turnId, `Q${q.index}`);
  }
  return map;
}

/** Prefer the last source bubble in a merged turn (usually the actual main-question ask). */
export function representativeTurnId(turn: ConversationTurn): string {
  const sources = turn.sourceIds;
  if (sources && sources.length > 0) return sources[sources.length - 1]!;
  return turn.id;
}

/** Deterministic turn / length stats from interviewing-phase conversational turns. */
export function computeTurnStats(transcript: ConversationTurn[]): Pick<
  ConversationAnalysis,
  'turns' | 'avgWords' | 'avgChars'
> {
  const interviewing = mergeConsecutiveSameRoleTurns(filterInterviewingTurns(transcript));
  const assistant = interviewing.filter((t) => t.role === 'assistant');
  const user = interviewing.filter((t) => t.role === 'user');

  return {
    turns: {
      total: interviewing.length,
      assistant: assistant.length,
      user: user.length,
    },
    avgWords: {
      assistant: average(assistant.map((t) => countWords(t.text))),
      user: average(user.map((t) => countWords(t.text))),
    },
    avgChars: {
      assistant: average(assistant.map((t) => countChars(t.text))),
      user: average(user.map((t) => countChars(t.text))),
    },
  };
}
